import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  isAuthenticated,
  logout as logoutUtil,
  isLoginRequired,
  isForceLoginEnabled,
  clearLoginState
} from '../utils/auth';

const AuthContext = createContext(null);

/**
 * AuthProvider - Manages authentication state throughout the application
 * Supports both persistent sessions (1-hour) and force login mode (login on every refresh)
 */
export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize from localStorage on mount
    if (!isLoginRequired()) {
      return true; // If login not required, consider user as logged in
    }

    // If force login is enabled, always start as logged out
    // This ensures login screen appears on every refresh
    if (isForceLoginEnabled()) {
      // Clean up any stale localStorage data from previous sessions
      clearLoginState();
      return false;
    }

    return isAuthenticated();
  });

  // Check authentication status on mount and set up interval
  useEffect(() => {
    if (!isLoginRequired()) {
      setIsLoggedIn(true);
      return;
    }

    // If force login is enabled, no need for interval checking
    // Auth state is only in memory and resets on refresh
    if (isForceLoginEnabled()) {
      return;
    }

    // Check authentication status
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setIsLoggedIn(authStatus);
    };

    // Check immediately
    checkAuth();

    // Set up interval to check auth status every minute
    // This handles the 1-hour session expiration
    const intervalId = setInterval(checkAuth, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Handle successful login
  const handleLoginSuccess = useCallback(() => {
    setIsLoggedIn(true);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    logoutUtil();
    setIsLoggedIn(false);
  }, []);

  const value = {
    isLoggedIn,
    login: handleLoginSuccess,
    logout: handleLogout,
    loginRequired: isLoginRequired(),
    forceLoginEnabled: isForceLoginEnabled(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth hook - Access authentication context
 * @returns {Object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
